import React from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../ui/accordion";

/* =====================================================================
   DEALER FAQ — Premium accordion (only one open at a time).
   Visual treatment matches the homepage FAQSection cards for site-wide
   consistency: rounded white cards, soft shadow, orange accent when
   expanded, smooth open/close animations via Radix Accordion.
   ===================================================================== */

const FAQS = [
  {
    q: "Who can become a LIFTILE dealer?",
    a: "Individuals, businesses and construction equipment suppliers who are passionate about building long-term customer relationships are welcome to apply.",
  },
  {
    q: "Is previous construction equipment experience required?",
    a: "Previous experience is preferred but not mandatory. We provide product knowledge and technical guidance to help our dealers succeed.",
  },
  {
    q: "What support does LIFTILE provide to dealers?",
    a: "We provide product training, marketing support, technical assistance, spare parts support and after-sales guidance.",
  },
  {
    q: "How long does the dealership approval process take?",
    a: "The approval timeline depends on your location and business profile. Our team will keep you informed throughout the process.",
  },
  {
    q: "How do I apply?",
    a: "Simply complete the dealership enquiry form on this page. Our team will review your application and contact you to discuss the next steps.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

export default function DealerFAQ() {
  return (
    <section
      data-testid="dealer-faq"
      className="relative bg-liftile-cream py-24 md:py-32 overflow-hidden"
    >
      {/* Subtle blueprint grid */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.5) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />
      {/* Soft orange ambient accent */}
      <div
        aria-hidden
        className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full pointer-events-none opacity-[0.18]"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(40px)",
        }}
      />

      <div className="container-x relative">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={fadeUp}
          className="max-w-3xl mx-auto text-center"
        >
          <div className="heading-eyebrow mb-4 text-liftile-orange">Knowledge Base</div>
          <h2
            data-testid="dealer-faq-heading"
            className="font-display uppercase text-liftile-matte-black leading-[0.98] tracking-tight text-4xl sm:text-5xl md:text-[3.25rem]"
          >
            Dealer Frequently Asked{" "}
            <span className="text-liftile-orange">Questions</span>
          </h2>
          <p className="font-body text-liftile-matte-black/70 mt-6 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            Everything you need to know before becoming a LIFTILE business
            partner.
          </p>
        </motion.div>

        {/* Accordion */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={fadeUp}
          transition={{ delay: 0.15 }}
          className="max-w-3xl mx-auto mt-14 md:mt-16"
        >
          <Accordion
            type="single"
            collapsible
            defaultValue="dfaq-0"
            className="space-y-3"
          >
            {FAQS.map((item, i) => (
              <AccordionItem
                key={item.q}
                value={`dfaq-${i}`}
                data-testid={`dealer-faq-item-${i}`}
                className="group/item border-0 bg-white rounded-xl shadow-[0_2px_10px_-4px_rgba(0,0,0,0.08)] hover:shadow-[0_10px_28px_-8px_rgba(0,0,0,0.12)] transition-all duration-400 data-[state=open]:shadow-[0_18px_45px_-12px_rgba(255,107,26,0.28)] data-[state=open]:ring-1 data-[state=open]:ring-liftile-orange/60"
              >
                <AccordionTrigger
                  data-testid={`dealer-faq-trigger-${i}`}
                  className="group/trigger w-full hover:no-underline [&>svg]:hidden px-6 md:px-7 py-5 md:py-6 text-left"
                >
                  <div className="flex items-start gap-5 w-full">
                    <span className="shrink-0 mt-1 font-heading text-xs uppercase tracking-[0.2em] text-liftile-orange font-semibold w-8 group-data-[state=open]/item:opacity-100 opacity-70 transition-opacity">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="flex-1 font-heading text-base md:text-lg text-liftile-matte-black font-semibold leading-snug group-data-[state=open]/item:text-liftile-orange transition-colors duration-300">
                      {item.q}
                    </span>
                    <span
                      aria-hidden
                      className="shrink-0 grid place-items-center w-9 h-9 rounded-full border border-liftile-matte-black/10 text-liftile-matte-black/70 group-data-[state=open]/item:bg-liftile-orange group-data-[state=open]/item:text-white group-data-[state=open]/item:border-liftile-orange group-data-[state=open]/item:rotate-45 transition-all duration-400 ease-out"
                    >
                      <Plus className="w-4 h-4" strokeWidth={2.5} />
                    </span>
                  </div>
                </AccordionTrigger>

                <AccordionContent className="px-6 md:px-7 pb-6 md:pb-7 pt-0">
                  <div style={{ paddingLeft: "3.25rem" }}>
                    <div className="h-px bg-liftile-orange/30 mb-4" />
                    <p className="font-body text-sm md:text-base text-liftile-matte-black/75 leading-relaxed">
                      {item.a}
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
